import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ListChecks, Loader2, ArrowRight } from "lucide-react";
import { SEV_STYLE } from "./shared";

const PRI_STYLE = { critical: SEV_STYLE.critical, high: SEV_STYLE.high, medium: SEV_STYLE.medium, low: SEV_STYLE.low };

export function DailyActionPlan({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    execService.actionPlan(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [params.tenantId]);

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="action-plan-panel">
      <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-primary" /> Daily Action Plan</h2>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !data?.items?.length ? (
        <p className="text-xs text-muted-foreground">Nothing urgent today — you're all caught up.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((a) => (
            <Link to={a.link || "#"} key={a.id} className="flex items-start gap-2 border border-border rounded-lg p-3 hover:border-primary/40 transition-colors" data-testid={`action-${a.id}`}>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${PRI_STYLE[a.priority] || PRI_STYLE.low} shrink-0`}>{a.priority}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground font-medium">{a.title}</p>
                {a.detail && <p className="text-[11px] text-muted-foreground">{a.detail}</p>}
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">{a.source}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExecutiveAlerts({ params, onDrill }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    execService.alerts(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [params.tenantId]);

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="alerts-panel">
      <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3">Executive Alerts</h2>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !data?.items?.length ? (
        <p className="text-xs text-muted-foreground">No active alerts. All clear.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((a) => (
            <button key={a.id} onClick={a.drilldown ? () => onDrill?.(a.drilldown, a.title) : undefined}
              className={`w-full text-left flex items-center gap-2 border rounded-lg p-3 ${a.drilldown ? "hover:border-primary/40 cursor-pointer" : "cursor-default"} ${SEV_STYLE[a.severity]} border-current/30`} data-testid={`alert-${a.id}`}>
              <span className="text-[10px] font-bold uppercase shrink-0">{a.severity}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{a.title}</p>
                <p className="text-[11px] text-muted-foreground">{a.detail}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
